/**
 * Rex Mortis — Interactive tutorial walkthrough
 *
 * A floating coach-mark panel that walks new players through the core game
 * loop. Progress is persisted server-side as Character.tutorialStep
 * (0 = not started, 1..N = current step, 99 = done). The step count is driven
 * by STEPS.length, so add/remove steps freely.
 *
 * Steps (reordered 2026-06-12: the social beats moved UP — "say hello" was
 * dead last and Houses weren't in the guide at all, which taught newcomers
 * every system except the people):
 *   1. Commit a misdeed        8. Take up a trade
 *   2. Or do a good deed       9. Keep your coin safe (bank)
 *   3. Say hello (shoutbox)   10. Sail abroad (travel)
 *   4. Train your body        11. Work the land (field trades)
 *   5. Spar against NPCs      12. Craft at the stations
 *   6. Seek a House           13. Strike the Crypt denizen
 *   7. Check your inventory   14. Race at the Racing Yard
 *
 * NOTE: STEPS is keyed by `number`, not array position — the shout entry
 * lives at the end of the array but renders as step 3.
 *
 * Unlike WelcomeTour (a one-shot intro modal), this is a persistent guide
 * that floats in the corner until the player completes every step or
 * dismisses it (which sets step=99). Settings → "Replay the tutorial" resets
 * tutorialStep to 0 to show it again.
 */

import { api } from '../api/client.js';
import { store } from '../state/store.js';
import { showConfirmDialog } from './ConfirmDialog.js';
import { onTutorialSignal, type TutorialSignal } from '../util/tutorialBus.js';

interface Step {
  number: number;
  title: string;
  body: string;
  ctaLabel: string;
  ctaHref: string;
  /** The action signal (emitted by the API client on success) that completes
   *  this step automatically — see util/tutorialBus.ts. */
  signal: TutorialSignal;
}

const STEPS: readonly Step[] = [
  {
    number: 1,
    title: 'Commit a misdeed',
    body:
      'Misdeeds are the crooked road to coin. Open the page and start with <strong>Banditry</strong> — ' +
      '“Beg in the Square” or “Cutpurse” are the gentlest. Each attempt spends <strong>Resolve</strong> and ' +
      'rolls Triumph, Partial, Foiled, or Disastrous. Watch the red <em>Dungeon</em> risk on each card: a Disastrous ' +
      'roll gets you jailed. Higher Cunning shifts the odds your way.',
    ctaLabel: 'Open Misdeeds',
    ctaHref: '#/misdeeds',
    signal: 'misdeed',
  },
  {
    number: 2,
    title: 'Or do a good deed',
    body:
      'Prefer the honest path? <strong>Good Deeds</strong> mirror Misdeeds — spend Resolve to earn Crowns, ' +
      '<strong>Virtue</strong> (the skill that gates them) and <strong>Honour</strong> (your good name). Crime pays ' +
      'better coin but answers to the Dungeon; a foiled deed risks only the <em>Infirmary</em>. Both paths level you at the same rate, and each has its own awards.',
    ctaLabel: 'Open Good Deeds',
    ctaHref: '#/good-deeds',
    signal: 'goodDeed',
  },
  {
    number: 4,
    title: 'Train your body',
    body:
      'Spend <strong>Vigor</strong> at the Training Yard to raise your battle stats — Strength, Defense, Speed and ' +
      'Dexterity. Higher yard tiers multiply what you gain, but demand higher minimum stats to enter. Your four bars ' +
      '(Vigor, Resolve, Spirit, Life) refill over time, so spend them freely.',
    ctaLabel: 'Open Training Yard',
    ctaHref: '#/training',
    signal: 'train',
  },
  {
    number: 5,
    title: 'Prove yourself in the Sparring Hall',
    body:
      'With a few hundred stat points, fight the Sparring Hall’s NPCs for XP, crowns and loot. Each card shows the ' +
      'opponent’s stats and the reward; mind the per-opponent cooldown after a win. Lose and you’ll be carried to the Infirmary.',
    ctaLabel: 'Open Sparring Hall',
    ctaHref: '#/sparring',
    signal: 'spar',
  },
  {
    number: 6,
    title: 'Seek a House',
    body:
      'The realm is not meant to be walked alone. A <strong>House</strong> shares a treasury, a chain of ' +
      'victories, its own hall of talk, and a perk tree that strengthens every member. Browse the Houses and ' +
      'petition one that suits your road — or, in time, raise your own.',
    ctaLabel: 'Browse the Houses',
    ctaHref: '#/houses',
    signal: 'house',
  },
  {
    number: 7,
    title: 'Check your inventory',
    body:
      'Everything you loot, craft or are gifted lands in your <strong>Inventory</strong>. From there you equip weapons ' +
      'and armour, use tonics to restore your bars, or list spare goods on the Merchant’s Market for passive coin.',
    ctaLabel: 'Open Inventory',
    ctaHref: '#/inventory',
    signal: 'inventory',
  },
  {
    number: 8,
    title: 'Take up a trade',
    body:
      'The Trades hall pays a daily wage that keeps coming <strong>even while you’re logged off</strong>. ' +
      'Honest coin that stacks with everything else — claim it whenever you return.',
    ctaLabel: 'Open Trades',
    ctaHref: '#/trades',
    signal: 'trade',
  },
  {
    number: 9,
    title: 'Keep your coin safe',
    body:
      'Crowns in your wallet can be <strong>plundered</strong> by other players. Deposit anything you’re not spending ' +
      'in the <strong>Counting House</strong> — it earns interest at your tier’s daily rate and can’t be stolen.',
    ctaLabel: 'Open Counting House',
    ctaHref: '#/banks',
    signal: 'bank',
  },
  {
    number: 10,
    title: 'Sail abroad',
    body:
      'Visit <strong>The Roads</strong> to sail to foreign kingdoms and buy goods you can’t get at home — carry them back ' +
      'to sell. A finer ship from the <strong>Royal Shipyard</strong> shortens every crossing, and each city’s stock is ' +
      'shared and limited, so the rarest goods are a race.',
    ctaLabel: 'Open The Roads',
    ctaHref: '#/travel',
    signal: 'travel',
  },
  {
    number: 11,
    title: 'Work the land',
    body:
      'Every foreign city plies a <strong>field trade</strong> at its own named spot — fishing, hunting, mining, and more, ' +
      'eight in all. When you arrive somewhere, the city’s trade panel sits right on The Roads page: spend Vigor to gather, ' +
      'level the skill, and keep what you haul. <strong>Bare hands work fine</strong> — a proper tool just betters the odds.',
    ctaLabel: 'Open The Roads',
    ctaHref: '#/travel',
    signal: 'gather',
  },
  {
    number: 12,
    title: 'Craft your haul into coin',
    body:
      'What you gather feeds four workshops: the <strong>Forge</strong> (gear), the <strong>Apothecary</strong> (remedies), ' +
      'the <strong>Cookfire</strong> (dishes that restore your bars), and the <strong>Brewhouse</strong> (drink). Crude and ' +
      'Common recipes are open to all — the finer ones unlock as you climb a crafting vocation at the Trades hall.',
    ctaLabel: 'Open the Forge',
    ctaHref: '#/forge',
    signal: 'craft',
  },
  {
    number: 13,
    title: 'Strike the Crypt denizen',
    body:
      'Each week a horror rises in <strong>The Crypt</strong>, and the whole realm fells it together. Strikes spend Vigor, ' +
      'deal the same damage for everyone, and carry <strong>no danger to you</strong> — and a slain denizen pays out crowns ' +
      'and loot by the damage you dealt. Even a newcomer’s blows count.',
    ctaLabel: 'Enter the Crypt',
    ctaHref: '#/crypt',
    signal: 'crypt',
  },
  {
    number: 14,
    title: 'Race at the Racing Yard',
    body:
      'Buy a horse from the Stablemaster, then enter it in races for crowns, XP, and <strong>Racing Prestige</strong>. ' +
      'Each breed favours a surface and distance, horses level up by racing, and you can forge gear to make them faster. ' +
      'And with that, the tour is done — welcome to Rex Mortis.',
    ctaLabel: 'Open the Racing Yard',
    ctaHref: '#/racing',
    signal: 'race',
  },
  // Renders as step 3 — see the NOTE in the header comment.
  {
    number: 3,
    title: 'Say hello',
    body:
      'The <strong>Shoutbox</strong> is the realm’s open chamber, and the realm is small enough that a hello ' +
      'gets answered. Drop one to mark your arrival — and watch the bell at the top right for incoming attacks ' +
      'and messages.',
    ctaLabel: 'Open the Shoutbox',
    ctaHref: '#/shoutbox',
    signal: 'shout',
  },
];

// Collapsed-state key is scoped per account so a shared browser (or a fresh
// character registered on a machine that played before) doesn't inherit the
// previous player's UI state.
function collapseKey(): string {
  return `rm-tutorial-collapsed.${store.get().account?.id ?? 'anon'}`;
}

function currentStepNumber(): number {
  const char = store.get().character;
  if (!char) return 0;
  if (char.tutorialStep === 0) return 1; // never started → show step 1
  if (char.tutorialStep >= 99) return 99; // done
  return char.tutorialStep;
}

// Auto-advance: when the API client reports a successful action that matches
// the CURRENT step, mark it done and move on — the player did the thing, the
// guide should notice without a manual "Mark done" click. Wired exactly once.
let busWired = false;
function wireTutorialBus(): void {
  if (busWired) return;
  busWired = true;
  onTutorialSignal((signal) => {
    void (async () => {
      const step = currentStepNumber();
      if (step < 1 || step > STEPS.length) return;
      const def = STEPS.find((s) => s.number === step);
      if (!def || def.signal !== signal) return;
      await advanceStep(step + 1 > STEPS.length ? 99 : step + 1);
      mountInteractiveTutorial();
    })();
  });
}

/** Idempotent — mounts the floating panel into the body if absent. */
export function mountInteractiveTutorial(): void {
  wireTutorialBus();
  const step = currentStepNumber();
  if (step === 99) {
    const existing = document.getElementById('rm-tutorial-panel');
    existing?.remove();
    return;
  }

  let panel = document.getElementById('rm-tutorial-panel');
  if (!panel) {
    panel = document.createElement('div');
    panel.id = 'rm-tutorial-panel';
    panel.setAttribute('role', 'complementary');
    // NOTE: use real design tokens. The old --c-paper/--c-border don't exist,
    // so the background resolved to transparent (the "glitch" overlay look).
    panel.style.cssText = `
      position: fixed;
      bottom: 16px;
      right: 16px;
      width: min(340px, calc(100vw - 24px));
      background: var(--c-parchment);
      color: var(--c-ink);
      border: 1px solid var(--c-rule);
      border-radius: 10px;
      box-shadow: 0 6px 28px rgba(0, 0, 0, 0.45);
      padding: 14px 16px;
      z-index: 2000;
      font-size: 0.95rem;
    `;
    document.body.appendChild(panel);
  }

  renderPanel(panel, step);
}

function renderPanel(panel: HTMLElement, stepNumber: number): void {
  let collapsed = false;
  try {
    collapsed = localStorage.getItem(collapseKey()) === '1';
  } catch {
    // storage unavailable — show expanded
  }
  const step = STEPS.find((s) => s.number === stepNumber) ?? STEPS[0]!;

  if (collapsed) {
    panel.innerHTML = `
      <button id="rm-tut-expand" class="btn ghost" style="font-size: 0.85rem;">
        Show tutorial (step ${stepNumber}/${STEPS.length})
      </button>
    `;
    panel.querySelector<HTMLButtonElement>('#rm-tut-expand')?.addEventListener('click', () => {
      try {
        localStorage.removeItem(collapseKey());
      } catch { /* ignore */ }
      renderPanel(panel, stepNumber);
    });
    return;
  }

  panel.innerHTML = `
    <div style="display:flex; align-items:baseline; justify-content:space-between; gap: 8px;">
      <strong style="font-family: var(--font-elegant); font-size: 1.05rem;">Step ${stepNumber} of ${STEPS.length}</strong>
      <div style="display:flex; gap: 4px;">
        <button id="rm-tut-collapse" class="btn ghost" style="padding: 2px 8px; font-size: 0.8rem;" title="Hide for now">_</button>
        <button id="rm-tut-skip" class="btn ghost" style="padding: 2px 8px; font-size: 0.8rem;" title="Skip the tutorial">×</button>
      </div>
    </div>
    <h4 style="margin: 8px 0 4px 0;">${step.title}</h4>
    <p style="margin: 0 0 10px 0; line-height: 1.4;">${step.body}</p>
    <div style="display:flex; gap: 8px; align-items:center;">
      <a href="${step.ctaHref}" class="btn btn-primary" id="rm-tut-cta">${step.ctaLabel}</a>
      <button class="btn ghost" id="rm-tut-done" style="font-size: 0.85rem;">Mark done</button>
    </div>
    <div style="margin-top: 10px; display:flex; gap: 3px;">
      ${Array.from({ length: STEPS.length }, (_, i) => i + 1)
        .map(
          (n) => `<span style="
            flex: 1;
            height: 4px;
            background: ${n <= stepNumber ? 'var(--c-gold)' : 'var(--c-rule)'};
            border-radius: 2px;
          "></span>`,
        )
        .join('')}
    </div>
  `;

  panel.querySelector<HTMLButtonElement>('#rm-tut-collapse')?.addEventListener('click', () => {
    try {
      localStorage.setItem(collapseKey(), '1');
    } catch { /* ignore */ }
    renderPanel(panel, stepNumber);
  });

  panel.querySelector<HTMLButtonElement>('#rm-tut-skip')?.addEventListener('click', async () => {
    if (!(await showConfirmDialog({ title: 'Skip the tutorial?', message: 'Skip the tutorial? You can always re-read these pages on your own.', confirmLabel: 'Skip', tone: 'gold' }))) return;
    await advanceStep(99);
    panel.remove();
  });

  // NOTE: the CTA is pure navigation. The step completes when the action
  // actually succeeds (see wireTutorialBus) or when the player clicks
  // "Mark done" — opening a page is not doing the thing.

  panel.querySelector<HTMLButtonElement>('#rm-tut-done')?.addEventListener('click', async () => {
    await advanceStep(stepNumber + 1 > STEPS.length ? 99 : stepNumber + 1);
    if (stepNumber + 1 > STEPS.length) {
      panel.remove();
    } else {
      mountInteractiveTutorial();
    }
  });
}

async function advanceStep(next: number): Promise<void> {
  try {
    const r = await api.setTutorialStep(next);
    // Patch the store so the panel reads fresh state.
    const s = store.get();
    if (s.character) {
      store.set({
        ...s,
        character: { ...s.character, tutorialStep: r.tutorialStep },
      });
    }
  } catch {
    // Never block the user on tutorial state.
  }
}
